import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import Loading from 'src/components/Loading';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import getFullAddressString from 'src/util/address';
import {
  FEATURE_INHIBIT_COMMUNITY_TRADES,
  FEATURE_INHIBIT_CONTRACTED_TRADES,
  FEATURE_INHIBIT_NOMINATED_TRADES,
} from 'src/util/constants';

import CommunityTradeRules from './CommunityTradeRules';
import ContractedTradeRules from './ContractedTradeRules';
import NominatedTradeRules from './NominatedTradeRules';
import ProposedTradeRules from './ProposedTradeRules';
import TradeRuleActiveResidual from './ResidualTradeRules';

const TradeRuleActiveFragment = graphql`
  fragment TradeRuleActive_property on Property {
    uuid
    title
    address {
      line1
      line2
      city
      state
      postcode
      gps {
        latitude
        longitude
      }
    }
    ...CommunityTradeRules_property
    ...ContractedTradeRules_property
    ...NominatedTradeRules_property
    ...ProposedTradeRules_property
    ...ResidualTradeRules_property
  }
`;

/**
 * TradeRuleActive functional component displays active trade rules for a property.
 * @param {object} props - Component props
 * @param {object} props.property - Property data from GraphQL fragment
 * @returns {React.ReactElement} The TradeRuleActive component
 */
function TradeRuleActive({ property = null }) {
  const intl = useIntl();

  if (!property) {
    return <Loading />;
  }

  const propertyData = useFragment(TradeRuleActiveFragment, property);

  const { address, uuid, title } = propertyData;

  // i18n variables
  const allTradeRulesLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.all_trade_rules.label', defaultMessage: 'All trade rules' });
  const propertySettingsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.property_settings.label', defaultMessage: 'Property Settings' });
  const pageTitle = intl.formatMessage(
    { id: 'trade_rule.trade_rule_active.page.title', defaultMessage: 'Enosi - Property: {propertyTitle}' },
    { propertyTitle: title },
  );
  const propertiesBreadcrumb = intl.formatMessage({ id: 'trade_rule.trade_rule_active.properties.breadcrumb', defaultMessage: 'Properties' });
  const tradeRulesBreadcrumb = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rules.breadcrumb', defaultMessage: 'Trade Rules' });
  const tradeRulesTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rules.title', defaultMessage: 'Trade Rules' });

  const renderPropertyControls = () => (
    <ButtonToolbar>
      <ButtonGroup className="ms-2">
        <Link to={`/properties/${uuid}/trade-rules`} className="btn btn-darken">
          {allTradeRulesLabel}
        </Link>
      </ButtonGroup>

      <ButtonGroup className="ms-2">
        <Link to={`/properties/${uuid}/settings`} className="btn btn-darken">
          {propertySettingsLabel}
        </Link>
      </ButtonGroup>
    </ButtonToolbar>
  );

  const addr = getFullAddressString(address);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          {
            name: propertiesBreadcrumb, path: '/properties',
          },
          { name: title, path: `/properties/${uuid}` },
          { name: tradeRulesBreadcrumb },
        ]}
      />
      <Heading
        title={title}
        subtitle={addr}
        context={tradeRulesTitle}
        controls={renderPropertyControls()}
      />
      {!APIConfig().feature(FEATURE_INHIBIT_CONTRACTED_TRADES) && (
        <ContractedTradeRules property={propertyData} />
      )}
      {!APIConfig().feature(FEATURE_INHIBIT_NOMINATED_TRADES) && (
        <>
          <ProposedTradeRules property={propertyData} />
          <NominatedTradeRules property={propertyData} />
        </>
      )}
      {!APIConfig().feature(FEATURE_INHIBIT_COMMUNITY_TRADES) && (
        <CommunityTradeRules property={propertyData} />
      )}
      <TradeRuleActiveResidual property={propertyData} />
    </>
  );
}

TradeRuleActive.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default TradeRuleActive;
